import type { PropsWithChildren } from 'react';
import { Component } from 'react';

import { LoadingPage } from 'platform/components/LoadingPage';
import { UnauthenticatedError, UnauthorizedError } from 'platform/Error';
import { getLoginUrl } from 'platform/loginRedirect';
// eslint-disable-next-line no-restricted-imports -- top level error boundary to handle error
import { WrongAccountPage } from 'sellDirect/routes/errors/WrongAccountPage';
// eslint-disable-next-line no-restricted-imports -- top level error boundary to handle error
import { NotAuthorizedPage } from 'shared/routes/errors/NotAuthorizedPage';

import navigationHelpers from './navigationHelpers';

type CustomerBoundaryProps = PropsWithChildren & {
  authenticationError?: UnauthenticatedError;
  authorizationError?: UnauthorizedError;
};

/**
 * Implements componentDidCatch to track the error in observability to handle unauthenticated errors.
 *
 * Rethrows the error if it is not an UnauthenticatedError.
 */
export class CustomerBoundary extends Component<
  CustomerBoundaryProps,
  {
    authenticationError?: UnauthenticatedError;
    authorizationError?: UnauthorizedError;
    hasError: boolean;
  }
> {
  /** Calls super */
  constructor(props: CustomerBoundaryProps) {
    super(props);

    if (props.authenticationError) {
      this.state = {
        authenticationError: props.authenticationError,
        authorizationError: undefined,
        hasError: true,
      };
    } else if (props.authorizationError) {
      this.state = {
        authorizationError: props.authorizationError,
        authenticationError: undefined,
        hasError: true,
      };
    }
  }

  static getDerivedStateFromError(error: UnauthenticatedError | UnauthorizedError | Error) {
    if (error instanceof UnauthenticatedError || error instanceof UnauthorizedError) {
      // Update state so the next render will show the fallback UI.
      return {
        hasError: true,
        authenticationError: error instanceof UnauthenticatedError ? error : undefined,
        authorizationError: error instanceof UnauthorizedError ? error : undefined,
      };
    }
  }

  /** @throws OD Team Boundary Error */
  componentDidCatch(error: UnauthenticatedError | UnauthorizedError | Error) {
    if (error instanceof UnauthenticatedError) {
      this.setState({ authenticationError: error, hasError: true });
    } else if (error instanceof UnauthorizedError) {
      this.setState({ authorizationError: error, hasError: true });
    } else {
      // Just forward error has already been caught once by boundary
      throw error;
    }
  }

  render() {
    return <CustomerBoundaryUI {...this.state}>{this.props.children}</CustomerBoundaryUI>;
  }
}

export class CustomerBoundaryUI extends Component<CustomerBoundaryProps> {
  render() {
    if (this.props.authorizationError) {
      const error = this.props.authorizationError;
      if (error.accessToken) {
        this.redirectToLogin(this.props.authorizationError);
        return <LoadingPage />;
      }

      // If the error is related to accessing a sell direct offer, we show a different error page
      if (
        error.triggeredQuery === 'LatestSellerLead' &&
        error.accessErrorType === 'permission-denied'
      ) {
        return <WrongAccountPage />;
      }

      if (error.accessErrorType === 'reauthentication-required') {
        this.logout();
        // Renders loading page while the user is redirected to the logout page
        return <LoadingPage />;
      }

      return <NotAuthorizedPage />;
    }

    if (this.props.authenticationError) {
      this.redirectToLogin(this.props.authenticationError);
      return <LoadingPage />;
    }

    return this.props.children;
  }

  /**
   * Redirects the user to the login page with the appropriate query params.
   */
  redirectToLogin = (error: UnauthenticatedError | UnauthorizedError) => {
    let label = 'login';
    if (error.accessToken) {
      // If there is no user but there is an `access_token` param in the URL, we will
      // redirect to the login lite url (opendoor.com/token)
      label = 'login-login-lite';
    } else if ('emailPrefill' in error && !!error.emailPrefill) {
      // If the user is not authenticated we redirect to the login page.
      // If there is an `emailPrefill` param in the URL, we add it as a query param
      // to pre-populate the log-in/sign-up form
      label = 'login-email-prefill';
    }

    if (typeof window !== 'undefined') {
      // Redirect customers to the login page if they are not authenticated
      navigationHelpers.hardNavReplace(getLoginUrl(window.location.href), {
        label,
        data: { ...error },
      });
    }
  };

  /**
   * Redirects the user to the login page with the appropriate query params.
   */
  logout = () => {
    if (typeof window !== 'undefined') {
      navigationHelpers.hardNavReplace(getLoginUrl(window.location.href));
    }
  };
}
